/**
 * Diäten-Plausibilität (2026-06-10, Thomas) — Pendant zur Stundenlisten-
 * cellPlausibility: Felder mit Autocompletion (Datum, Abfahrt, Rückkehr)
 * müssen nach der Normalisierung ein gültiges Format haben, sonst ROT +
 * Validieren gesperrt ("wenn beim Datumfeld kein Datum-Wert gelesen wurde,
 * muss das gemeldet werden und ein Validieren ist nicht möglich").
 *
 * Regeln:
 *  - Leere Zeilen sind OK (fixes 22-Zeilen-Grid, leere bleiben leer).
 *  - Zeile MIT Inhalt aber ohne gültiges Datum → rot am Datum.
 *  - Datum: gültig wenn bereits DD.MM.YYYY ODER via formatDateInput
 *    normalisierbar (dann zeigt der Tooltip den Vorschlag).
 *  - Abfahrt/Rückkehr: gültig wenn HH:MM ODER via formatTimeInput
 *    normalisierbar; Rückkehr ≤ Abfahrt → rot (Reihenfolge).
 */
import { formatTimeInput } from "./timeFormat";
import { formatDateInput } from "./dateFormat";

const DATE_CANONICAL = /^\d{2}\.\d{2}\.\d{4}$/;
const TIME_CANONICAL = /^\d{2}:\d{2}$/;

// Fahrtenbuch-Zellen (#469) tragen ROHE Zahlen (km/mitfahrer sind int, nicht
// {value}-Cells) — deshalb `number` zugelassen; sonst identisch zu den Diäten-Cells.
type EntryCell = { value: string; confidence?: number } | string | number | null | undefined;
type Entry = Record<string, EntryCell>;

function val(c: EntryCell): string {
  if (c == null) return "";
  if (typeof c === "string") return c;
  if (typeof c === "number") return Number.isFinite(c) ? String(c) : "";
  return c.value ?? "";
}

function rowHasContent(row: Entry): boolean {
  // AUSSCHLIESSLICH 3 Felder (2026-06-10, Thomas) — Altdaten können weitere
  // Schlüssel tragen, aber nur die digitalisierten Felder zählen als Inhalt.
  return ["datum", "abfahrt", "rueckkehr"]
    .some((k) => val(row[k]).trim().length > 0);
}

function dateIssue(raw: string, cardYear?: string | null): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (DATE_CANONICAL.test(s)) return null;
  const normalized = formatDateInput(s, cardYear);
  if (DATE_CANONICAL.test(normalized)) {
    return null; // normalisierbar — Autocompletion auf blur erledigt den Rest
  }
  return `„${s}“ ist kein Datum (erwartet TT.MM.JJJJ, z.B. 16.03.2026)`;
}

function timeIssue(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (TIME_CANONICAL.test(s)) return null;
  const normalized = formatTimeInput(s);
  if (TIME_CANONICAL.test(normalized)) return null;
  return `„${s}“ ist keine Uhrzeit (erwartet HH:MM, z.B. 08:30)`;
}

/** Issues einer Zeile: Map Feld → Begründung (nur rote Befunde). */
export function expenseRowIssues(row: Entry, cardYear?: string | null): Record<string, string> {
  const issues: Record<string, string> = {};
  if (!rowHasContent(row)) return issues;

  const datum = val(row.datum);
  if (!datum.trim()) {
    issues.datum = "Zeile hat Inhalt, aber kein Datum — Datum eintragen (TT.MM.JJJJ)";
  } else {
    const d = dateIssue(datum, cardYear);
    if (d) issues.datum = d;
  }

  const ab = val(row.abfahrt);
  const rk = val(row.rueckkehr);
  const abIssue = timeIssue(ab);
  const rkIssue = timeIssue(rk);
  if (abIssue) issues.abfahrt = abIssue;
  if (rkIssue) issues.rueckkehr = rkIssue;

  // Reihenfolge nur prüfen, wenn beide Zeiten formal gültig sind.
  if (!abIssue && !rkIssue && ab.trim() && rk.trim()) {
    const a = formatTimeInput(ab);
    const r = formatTimeInput(rk);
    if (TIME_CANONICAL.test(a) && TIME_CANONICAL.test(r) && r <= a) {
      issues.rueckkehr = `Rückkehr (${r}) muss nach Abfahrt (${a}) liegen`;
    }
  }
  return issues;
}

// ────────────────────────────────────────────────────────────────────────
// Fahrtenbuch km-Zeilen (#469) — eigene Plausibilität statt der Diäten-3-Feld-
// Prüfung. Physische Konsistenz der km-Stände (Audit-Netz, ocr/fahrtenbuch.py):
//   - km_ankunft ≥ km_abfahrt (Tacho läuft nicht rückwärts)
//   - gefahrene_km == km_ankunft − km_abfahrt (die gedruckte Differenz stimmt)
//   - nicht-numerische km-Werte (OCR-Müll wie „12ab") → rot.
// Datum/fahrtstrecke/zweck bleiben frei (kein Format-Zwang; das Fahrtenbuch-
// Datum ist DD.MM.JJ, nicht das Diäten-TT.MM.JJJJ).
// ────────────────────────────────────────────────────────────────────────

/** Schlüssel, die eine km-Zeile als „hat Inhalt" markieren. */
const FAHRTENBUCH_CONTENT_KEYS = [
  "datum", "km_abfahrt", "km_ankunft", "gefahrene_km", "fahrtstrecke", "zweck",
] as const;

function rowHasContentFahrtenbuch(row: Entry): boolean {
  return FAHRTENBUCH_CONTENT_KEYS.some((k) => val(row[k]).trim().length > 0);
}

/** km-Ganzzahl aus einer Zelle: entfernt Tausender-Punkte/Leerzeichen. Gibt die
 *  Zahl zurück, `null` bei leer, `NaN` bei nicht-parsebarem Rest (→ rot). */
function kmInt(c: EntryCell): number | null {
  const s = val(c).trim();
  if (!s) return null;
  if (!/^[\d.\s]+$/.test(s)) return NaN;      // Buchstaben/Sonderzeichen → ungültig
  const digits = s.replace(/[.\s]/g, "");
  if (!digits) return NaN;
  return parseInt(digits, 10);
}

/** km-Ganzzahl-Normalisierung fürs Speichern/onBlur: „12.345" → „12345",
 *  „  75 " → „75". Nicht-numerische Werte bleiben ROH (der Validator markiert
 *  sie rot und sperrt das Validieren). */
export function normalizeKmValue(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  if (/^[\d.\s]+$/.test(s)) {
    const digits = s.replace(/[.\s]/g, "");
    return digits || s;
  }
  return s;
}

/** Issues einer km-Zeile: Map Feld → Begründung (nur rote Befunde). */
export function fahrtenbuchRowIssues(row: Entry, cardYear?: string | null): Record<string, string> {
  const issues: Record<string, string> = {};
  if (!rowHasContentFahrtenbuch(row)) return issues;

  // 592-R2 (Thomas 24.07.): "ohne validiertem Datum kein Speichern" — Datum
  // ist beim Fahrtenbuch PFLICHT + Format TT.MM.JJJJ (die OCR normalisiert
  // DD.MM.JJ selbst; hier bleibt nur echter Muell rot und sperrt Validieren).
  const datum = val(row.datum);
  if (!datum.trim()) {
    issues.datum = "Zeile hat Inhalt, aber kein Datum — Datum eintragen (TT.MM.JJJJ)";
  } else {
    const d = dateIssue(datum, cardYear);
    if (d) issues.datum = d;
  }

  const ab = kmInt(row.km_abfahrt);
  const an = kmInt(row.km_ankunft);
  const gef = kmInt(row.gefahrene_km);

  if (Number.isNaN(ab)) issues.km_abfahrt = `„${val(row.km_abfahrt)}“ ist keine Kilometer-Zahl`;
  if (Number.isNaN(an)) issues.km_ankunft = `„${val(row.km_ankunft)}“ ist keine Kilometer-Zahl`;
  if (Number.isNaN(gef)) issues.gefahrene_km = `„${val(row.gefahrene_km)}“ ist keine Kilometer-Zahl`;

  const abOk = ab !== null && !Number.isNaN(ab);
  const anOk = an !== null && !Number.isNaN(an);
  const gefOk = gef !== null && !Number.isNaN(gef);

  // km_ankunft ≥ km_abfahrt (der Tacho läuft nicht rückwärts).
  if (abOk && anOk && (an as number) < (ab as number)) {
    issues.km_ankunft = `km Ankunft (${an}) muss ≥ km Abfahrt (${ab}) sein`;
  }

  // gefahrene_km == km_ankunft − km_abfahrt (nur wenn die Reihenfolge stimmt).
  if (abOk && anOk && gefOk && (an as number) >= (ab as number)) {
    const diff = (an as number) - (ab as number);
    if ((gef as number) !== diff) {
      issues.gefahrene_km = `gefahrene km (${gef}) ≠ km Ankunft − km Abfahrt (${diff})`;
    }
  }
  return issues;
}

/** Σ-Cross-Check (Layer B) fürs Banner: die aufgedruckte „Summe Kilometer"
 *  gegen die Summe der gelesenen Zeilen (ocr/fahrtenbuch.py `_cross_check`).
 *  Liest den vom Worker gerechneten `meta.layer_b_crosscheck`-Snapshot; `null`
 *  wenn nicht vorhanden (Altkarte / Layer-B-Fehler). */
export interface FahrtenbuchCrosscheck {
  zeilenSummeKm: number | null;
  aufgedrucktKm: number | null;
  diff: number | null;
  match: boolean;
  nZeilen: number | null;
}

/**
 * Σ der gefahrenen Kilometer aus den ZEILEN — ab 2026-08-12 der maßgebliche
 * Wert (Thomas nach Frankas erstem 3G-Testlauf: „rechnen soll damit die summe
 * der gefahrenen km sein und sich dynamisch anpassen bei korrektur").
 *
 * DAS DREHT LAYER A UND B UM. Bisher galt: aufgedruckte Monats-Summe = Lohnwert,
 * Zeilen = Audit-Netz. Der Anlassfall zeigt, warum das nicht trägt: die
 * aufgedruckte Summe ist EIN gelesenes Feld ohne jede Redundanz — auf prod
 * standen dort Werte wie 1, 4, 8 oder 70 gegen Zeilensummen von 1755, 1447,
 * 568, 976. Die Zeilen dagegen tragen km-Stand Abfahrt/Ankunft/gefahren und
 * sind am Scan einzeln prüfbar. Ab jetzt: Zeilen = Wert, aufgedruckt = Kontrolle.
 *
 * Reine Ableitung aus den AKTUELLEN Zellen — deshalb passt sich die Summe
 * sofort an, wenn eine Zeile korrigiert wird (kein OCR-Snapshot).
 *
 * `unlesbar` zählt Zeilen, deren km-Wert nicht als Zahl lesbar ist. Sie fließen
 * NICHT in die Summe, dürfen aber auch nicht still verschwinden — der Aufrufer
 * macht sie sichtbar (dieselbe fail-loud-Haltung wie bei den Zeilen-Befunden).
 */
export interface FahrtenbuchKmSummen {
  gefahren: number;
  mitfahrer: number;
  zeilen: number;
  unlesbar: number;
}

export function fahrtenbuchKmSummen(entries: ReadonlyArray<Entry> | null | undefined): FahrtenbuchKmSummen {
  const out: FahrtenbuchKmSummen = { gefahren: 0, mitfahrer: 0, zeilen: 0, unlesbar: 0 };
  if (!Array.isArray(entries)) return out;
  for (const row of entries) {
    if (!row || !rowHasContentFahrtenbuch(row)) continue;
    out.zeilen += 1;
    for (const [feld, ziel] of [["gefahrene_km", "gefahren"], ["mitfahrer", "mitfahrer"]] as const) {
      const n = kmInt(row[feld]);
      if (n === null) continue;          // leer = keine Angabe, kein Befund
      if (Number.isNaN(n)) { out.unlesbar += 1; continue; }
      out[ziel] += n;
    }
  }
  return out;
}

export function fahrtenbuchCrosscheck(data: unknown): FahrtenbuchCrosscheck | null {
  const cc = (data as { meta?: { layer_b_crosscheck?: unknown } } | null | undefined)
    ?.meta?.layer_b_crosscheck;
  if (!cc || typeof cc !== "object") return null;
  const c = cc as Record<string, unknown>;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return {
    zeilenSummeKm: num(c.zeilen_summe_km),
    aufgedrucktKm: num(c.aufgedruckt_km),
    diff: num(c.diff),
    match: c.match === true,
    nZeilen: num(c.n_zeilen),
  };
}

/**
 * Datum-Vererbung „wie oben“ (2026-06-10, Thomas) — Excel-artig: das Datum
 * der nächstgelegenen befüllten Zeile OBERHALB kann in eine Zeile übernommen
 * werden. Quelle muss selbst valide sein (kein Validator-Befund am Datum).
 */
export function inheritableDate(
  entries: Entry[],
  rowIdx: number,
  cardYear?: string | null,
): { value: string; fromRow: number } | null {
  for (let j = rowIdx - 1; j >= 0; j--) {
    const v = val(entries[j]?.datum).trim();
    if (!v) continue;
    if (dateIssue(v, cardYear)) return null; // nächste Quelle ist invalide — nicht „drüberspringen“
    return { value: formatDateInput(v, cardYear), fromRow: j };
  }
  return null;
}

/** Übernahme nur erlaubt, wenn das Zielfeld leer ist ODER ein Validator
 *  anschlagt (Thomas 2026-06-10) — gültige Eingaben werden nie überschrieben. */
export function canInheritDate(
  entries: Entry[],
  rowIdx: number,
  cardYear?: string | null,
): boolean {
  const cur = val(entries[rowIdx]?.datum).trim();
  if (!cur) return true;
  return dateIssue(cur, cardYear) !== null;
}

/** Alle Issues über alle Zeilen: Map "rowIndex.field" → Begründung.
 *  docType-aware (#469): `fahrtenbuch` → km-Konsistenz-Prüfung, sonst die
 *  Diäten-3-Feld-Prüfung. Ohne docType (Legacy-Aufrufer) → Diäten. */
export function expenseIssues(
  entries: Entry[],
  cardYear?: string | null,
  docType?: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  entries.forEach((row, idx) => {
    const issues = docType === "fahrtenbuch"
      ? fahrtenbuchRowIssues(row, cardYear)
      : expenseRowIssues(row, cardYear);
    for (const [field, reason] of Object.entries(issues)) {
      out[`${idx}.${field}`] = reason;
    }
  });
  return out;
}
